/** Decode ATS error.details and legacy FastAPI detail without exposing submitted values. */
export type AtsFieldValidationError = { field: string; label: string; message: string };

const FIELD_LABELS: Record<string, string> = {
  full_name: 'ФИО', phone: 'Телефон', phones: 'Телефон', email: 'Email', emails: 'Email',
  telegram: 'Telegram', links: 'Ссылки', date_of_birth: 'Дата рождения', age: 'Возраст',
  salary_expectation: 'Зарплатные ожидания', desired_position: 'Желаемая должность',
  location: 'Локация', location_city: 'Город', location_country: 'Страна', citizenship: 'Гражданство',
  gender: 'Пол', status: 'Статус', skills: 'Навыки', education: 'Образование',
  education_details: 'Образование', experience: 'Опыт работы', work_history: 'Опыт работы',
  tags: 'Теги', labels: 'Метки', custom_fields: 'Дополнительные поля', source: 'Источник',
};

const MESSAGES: Record<string, string> = {
  'Phone must contain from 10 to 15 digits': 'Номер должен содержать от 10 до 15 цифр. Пробелы, скобки, дефисы и знак + не считаются цифрами.',
  'Invalid email format': 'Введите корректный email, например name@example.com.',
  'Date of birth cannot be in the future': 'Дата рождения не может быть в будущем.',
  'Age must be between 0 and 120': 'Возраст должен быть от 0 до 120 лет.',
};

const TYPE_MESSAGES: Record<string, string> = {
  missing: 'Обязательное поле не заполнено.',
  string_type: 'Введите текст.', int_type: 'Введите целое число.', int_parsing: 'Введите целое число.',
  float_type: 'Введите число.', float_parsing: 'Введите число.',
  date_type: 'Укажите корректную дату.', date_parsing: 'Укажите корректную дату.',
  date_from_datetime_parsing: 'Укажите корректную дату.',
  list_type: 'Ожидается список значений.', dict_type: 'Неверный формат данных поля.',
  bool_type: 'Выберите значение «да» или «нет».', bool_parsing: 'Выберите значение «да» или «нет».',
};

export const parseAtsValidationErrors = (data: unknown): AtsFieldValidationError[] => {
  if (!data || typeof data !== 'object') return [];
  const record = data as Record<string, unknown>;
  const wrapped = record.error && typeof record.error === 'object' ? record.error as Record<string, unknown> : null;
  const details = wrapped?.details ?? record.detail;
  if (!Array.isArray(details)) return [];
  const errors: AtsFieldValidationError[] = [];
  for (const item of details) {
    if (!item || typeof item !== 'object' || !Array.isArray(item.loc) || typeof item.msg !== 'string') continue;
    const path = item.loc.filter((part: unknown) => typeof part === 'string' || typeof part === 'number');
    if (['body', 'query', 'path', 'header'].includes(path[0])) path.shift();
    const field = String(path[0] ?? '');
    const label = path.map((part: string | number) => typeof part === 'number' ? `№${part + 1}`
      : FIELD_LABELS[part] || `поле «${part}»`).join(' / ') || 'Данные формы';
    const rawMessage = item.msg.replace(/^Value error,\s*/i, '').trim();
    const message = MESSAGES[rawMessage] || TYPE_MESSAGES[item.type] || rawMessage || 'Проверьте значение поля.';
    // Scalar phone/email and their list aliases can report the same failure twice.
    if (!errors.some(error => error.label === label && error.message === message)) errors.push({ field, label, message });
  }
  return errors;
};

export const formatAtsValidationErrors = (errors: AtsFieldValidationError[]): string => (
  errors.length ? errors.map(error => `${error.label}: ${error.message}`).join('\n')
    : 'Проверьте заполнение полей. Сервер отклонил данные, но не указал причину.'
);

export class AtsCardValidationError extends Error {
  constructor(public readonly fieldErrors: AtsFieldValidationError[]) {
    super(formatAtsValidationErrors(fieldErrors));
    Object.setPrototypeOf(this, AtsCardValidationError.prototype);
  }
}
